import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Footer } from "../components/Footer";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function DataPolicyPage() {
  useEffect(() => {
    document.title = "Data Policy - Afro Fanthem";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <article className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8" data-testid="data-policy-headline">
            Data Policy
          </h1>

          <div className="prose prose-invert prose-gray max-w-none space-y-8">
            <p className="text-gray-300 text-lg leading-relaxed">
              Afro Fanthem collects fan responses to better understand the scale, culture, spending power, emotional investment, and identity of African football supporters.
            </p>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">What We Collect</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Through the African Football Fan Survey, we collect information including:
              </p>
              <ul className="list-disc list-inside text-gray-400 space-y-2">
                <li>Which club you support and how long you have supported them</li>
                <li>How you engage with your club (streaming, social media, merchandise, etc.)</li>
                <li>Your age group and country</li>
                <li>Your views on recognition, membership, and fan engagement</li>
                <li>Optional: email address, phone number (for prize draw entry), and open-text responses</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">Why We Collect It</h2>
              <p className="text-gray-400 leading-relaxed">
                The purpose of collecting this data is to help build a stronger case for the recognition of African football fans by clubs, leagues, media, brands, and wider football culture.
              </p>
              <p className="text-gray-400 leading-relaxed mt-4">
                Survey responses may be used to produce aggregated insights, campaign materials, fan reports, and public-facing summaries.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">The African Football Fan Report</h2>
              <p className="text-gray-400 leading-relaxed">
                We are collecting fan responses to build a public-facing African Football Fan Report. The aim is to show the scale, culture, spending power, and emotional investment of African supporters.
              </p>
              <p className="text-gray-400 leading-relaxed mt-4">
                Findings from the survey will be used in aggregated form to support the report and related campaign activity.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">Personal Details</h2>
              <p className="text-gray-400 leading-relaxed">
                Some personal details such as email and phone number are optional or required only for specific features such as the prize draw or account registration. You can complete the survey without providing a phone number.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">Individual Responses</h2>
              <p className="text-gray-400 leading-relaxed">
                Individual responses will not be published in a way that identifies a person unless clear permission is given. Open-text responses may be quoted anonymously in reports or campaign materials.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">Use by Partners</h2>
              <p className="text-gray-400 leading-relaxed">
                Data may help Afro Fanthem engage clubs, leagues, media, brands, and cultural partners on behalf of African football fans. Only aggregated, anonymised insights will be shared externally.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl text-white uppercase mb-4">Contact Us About Your Data</h2>
              <p className="text-gray-400 leading-relaxed">
                Data questions can be sent to:
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4 text-[#FBBF24]" />
                  <a href="mailto:afrofanthem@afrofanthem.com" className="text-[#FBBF24] hover:underline">afrofanthem@afrofanthem.com</a>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4 text-[#FBBF24]" />
                  <a href="mailto:admin@satraders.co.uk" className="text-[#FBBF24] hover:underline">admin@satraders.co.uk</a>
                </li>
              </ul>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/" className="inline-flex items-center gap-2 text-[#FBBF24] font-bold uppercase tracking-widest hover:gap-4 transition-all" data-testid="data-policy-back-link">
              <ArrowLeft className="w-5 h-5" />
              Back to Homepage
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
